import Q from 'q';

import { ServiceConfiguration } from './ServiceConfiguration';
import { IHttpService, IHttpResponse } from 'Core';
import {
  IReportDataService,
  IBallTrajectoryService,
  IReportDataConversionService,
  ReportDataServiceHelper,
} from 'Services';
import {
  IOptimization,
  IReportOptimization,
  IMultiGroupReport,
  IMeasurementReport,
  IDispersionEllipse,
  ICoordinate2D,
} from 'Data';
import { NormalizationSettingsType } from 'Components/NormalizationSettings/configs';

const baseUrl = ServiceConfiguration.getReportUrl();

export class MtmReportDataService implements IReportDataService {
  private http: IHttpService;
  private dataConversionService: IReportDataConversionService;
  private static reportData: IMeasurementReport;

  public static $inject = [
    'IHttpService',
    'IBallTrajectoryService',
    'IReportDataConversionService',
  ];

  constructor(
    http: IHttpService,
    ballTrajectoryService: IBallTrajectoryService,
    dataConversionService: IReportDataConversionService
  ) {
    this.http = http;
    this.dataConversionService = dataConversionService;
  }

  getReportData(
    reportId: string,
    normalizationSettings?: NormalizationSettingsType,
    forceRefetch?: boolean
  ): Q.Promise<[IMeasurementReport, boolean]> {
    return Q.all([
      this.getDynamicReportData(reportId, {
        normalizationSettings,
        forceRefetch,
      }),
      this.deviceOnline(),
    ]);
  }

  getOptimizationData(
    measurementId: Array<string>,
    optimizationType: string,
    reportId: string
  ): Q.Promise<IReportOptimization> {
    console.log('optimizationType ', optimizationType);
    console.log('reportId ', reportId);
    const defered = Q.defer<IReportOptimization>();
    const url = `${baseUrl}/Optimize`;
    const data = {
      ReportId: reportId,
      MeasurementIds: measurementId,
      SessionIds: [],
      OptimizationType: optimizationType,
    };
    this.http
      .post<IOptimization>(url, data)
      .then((response: IHttpResponse<IOptimization | string>) => {
        if (response.data) {
          const responseData = response.data as IOptimization;
          defered.resolve(this.dataConversionService.convertOptimizedData(responseData));
        }
      })
      .fail((reason: any) => {
        console.log(reason);
        defered.reject(null);
      })
      .done();
    return defered.promise;
  }

  private dispersionCacheRequest: Map<string, ICoordinate2D[]> = new Map<string, ICoordinate2D[]>();
  private dispersionCacheResponse: Map<string, IDispersionEllipse> = new Map<
    string,
    IDispersionEllipse
  >();

  getDispersionEllipse(
    coordinates: ICoordinate2D[],
    colorOfElllipsis: string
  ): Q.Promise<IDispersionEllipse> {
    const defered = Q.defer<IDispersionEllipse>();

    if ((this.dispersionCacheResponse as any)[colorOfElllipsis]) {
      const c = (this.dispersionCacheRequest as any)[colorOfElllipsis];
      if (ReportDataServiceHelper.compareDispersionCoordinates(c, coordinates)) {
        defered.resolve(c);
        return defered.promise;
      }
    }

    const url = `${baseUrl}/GetDispersionEllipse`;
    const data = { Coordinates: coordinates };
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    this.http
      .post<IDispersionEllipse>(url, data)
      .then((response: IHttpResponse<IDispersionEllipse | string>) => {
        const ellispe: IDispersionEllipse =
          (response.data as IDispersionEllipse) || JSON.parse(response.data as string);
        const angleInDegrees = -1 * (ellispe.Angle || 0) * (180 / Math.PI);
        ellispe.Angle = angleInDegrees;

        (self.dispersionCacheRequest as any)[colorOfElllipsis] = coordinates;
        (self.dispersionCacheResponse as any)[colorOfElllipsis] = ellispe;

        defered.resolve(ellispe);
      })
      .fail((reason: any) => {
        console.log(reason);
        defered.reject(null);
      })
      .done();
    return defered.promise;
  }

  shortenUrl(url: string): Q.Promise<string> {
    const defered = Q.defer<string>();
    const data = { Url: url };
    const serviceUrl = `${baseUrl}/ShortenUrl`;
    this.http
      .post<string>(serviceUrl, data)
      .then((response: IHttpResponse<string>) => {
        defered.resolve(response.data);
      })
      .fail(() => {
        defered.reject(null);
      })
      .done();
    return defered.promise;
  }

  private getDynamicReportData(
    reportId: string,
    {
      normalizationSettings,
      forceRefetch,
    }: { normalizationSettings?: NormalizationSettingsType; forceRefetch?: boolean } = {}
  ): Q.Promise<IMeasurementReport> {
    const defered = Q.defer<IMeasurementReport>();
    const url = `${baseUrl}/getreport`;
    const data = {
      ReportId: reportId,
      Altitude: normalizationSettings?.altitude,
      Temperature: normalizationSettings?.temperature,
      BallType: normalizationSettings?.ballType,
    };
    if (!forceRefetch && MtmReportDataService.reportData) {
      defered.resolve(MtmReportDataService.reportData);
    } else {
      this.http
        .post<IMultiGroupReport>(url, data)
        .then((response: IHttpResponse<IMultiGroupReport>) => {
          if (response.data) {
            const report = response.data as IMultiGroupReport;
            MtmReportDataService.reportData = this.dataConversionService.convertData(report);
            defered.resolve(MtmReportDataService.reportData);
          }
        })
        .fail((reason: any) => {
          console.log(reason);
          defered.reject(reason);
        })
        .done();
    }
    return defered.promise;
  }

  private deviceOnline(): Q.Promise<boolean> {
    const defered = Q.defer<boolean>();
    defered.resolve(true);
    return defered.promise;
  }
}
